import { cartSchema, PAYMENT_TYPE, type VasItem } from "@dransay/api/models";
import { z } from "zod";
import { unwrap } from "~/utils/zod";
import {
	CHECKOUT_STEP_NAMES,
	checkoutDiagnoseComplaints,
	checkoutDiagnoseStepToSchema,
	checkoutSteps,
	checkoutStepToSchema,
} from "./constants";
import { getDiagnoseSteps } from "./helpers";

export {
	type CheckoutCart,
	type CheckoutPatientData,
	checkoutPatientDataSchema,
	getCheckoutPatientDataSchema,
} from "@dransay/api/models";

export const getCheckoutAwarenessSchema = (
	{ selectOne } = { selectOne: "Select one of the answers to proceed." },
) =>
	z.object({
		isAddicted: z
			.string({ message: selectOne })
			.refine((val) => val !== "true"),
	});
export type CheckoutAwareness = z.infer<
	ReturnType<typeof getCheckoutAwarenessSchema>
>;

export const positiveEffects = [
	"pain_relief",
	"ease",
	"well_being",
	"sensitive",
	"detail_perception",
	"euphoria",
	"fooling_around",
	"empathy",
	"sense_of_community",
	"new_ideas",
	"none",
] as const;

export const negativeEffects = [
	"none",
	"dizziness",
	"heart_palpitations",
	"drowsiness",
	"circulatory_collapse",
	"nausea",
	"appetite_increase",
	"motor_problems",
	"unclear_thinking",
	"panic_or_anxiety",
	"paranoia",
	"hallucinations",
	"memory_gaps",
	"feeling_lonely",
] as const;

export const therapyEffectsSchema = z
	.object({
		positiveEffects: z.array(z.string()).optional(),
		negativeEffects: z.array(z.string()).optional(),
		symptomImprovement: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (
			!data.positiveEffects ||
			data.positiveEffects.length === 0 ||
			data.positiveEffects?.includes("none")
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "You must select at least one positive effect",
				path: ["positiveEffects"],
			});
		}

		const showNegativeEffects =
			!data.positiveEffects ||
			(data.positiveEffects.length > 0 &&
				!data.positiveEffects.includes("none"));

		if (
			showNegativeEffects &&
			(!data.negativeEffects || data.negativeEffects.length === 0)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "You must select a negative effect",
				path: ["negativeEffects"],
			});
		}
		if (showNegativeEffects && !data.negativeEffects?.includes("none")) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "You cannot continue treatment with negative effects",
				path: ["negativeEffects"],
			});
		}

		const showSymptomImprovement =
			showNegativeEffects && data.negativeEffects?.includes("none");

		if (showSymptomImprovement && !data.symptomImprovement) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "You must select a symptom improvement option",
				path: ["symptomImprovement"],
			});
		}

		if (
			showSymptomImprovement &&
			data.symptomImprovement &&
			!["alleviated", "greatly_relieved", "free_of_symptoms"].includes(
				data.symptomImprovement,
			)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					"Please select one of: Alleviated, Greatly relieved, or Free of symptoms",
				path: ["symptomImprovement"],
			});
		}
	});

export type TherapyEffects = z.infer<typeof therapyEffectsSchema>;

export const symptomsStarted = [
	"1_7_days_ago",
	"1_4_weeks_ago",
	"1_3_months_ago",
	"3_12_months_ago",
	"1_5_years_ago",
	"more_than_5_years_ago",
] as const;

export type SymptomStart = (typeof symptomsStarted)[number];

export const disallowedSymptomsStarted: SymptomStart[] = [
	"1_7_days_ago",
	"1_4_weeks_ago",
	"1_3_months_ago",
];

export const symptomsRecurrence = [
	"constantly",
	"daily",
	"weekly",
	"monthly",
	"less_often",
] as const;

export type SymptomRecurrence = (typeof symptomsRecurrence)[number];

export const disallowedSymptomsRecurrence: SymptomRecurrence[] = [
	"monthly",
	"less_often",
];

export const getSymptomsSchema = (
	{
		selectOne,
		cannotStartTherapySoSoon,
		cannotStartTherapyWithLowReccurence,
	} = {
		selectOne: "Select one of the answers to proceed.",
		cannotStartTherapySoSoon:
			"You cannot start therapy so soon after the symptoms started",
		cannotStartTherapyWithLowReccurence:
			"You cannot start therapy when the symptoms happen so rarely",
	},
) =>
	z
		.object({
			symptomsStarted: z.string({ message: selectOne }),
			symptomsRecurrence: z.string({ message: selectOne }),
		})
		.superRefine((data, ctx) => {
			if (!data.symptomsStarted) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: selectOne,
					path: ["symptomsStarted"],
				});
			}

			if (
				disallowedSymptomsStarted.includes(data.symptomsStarted as SymptomStart)
			) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: cannotStartTherapySoSoon,
					path: ["symptomsStarted"],
				});
			}

			if (!data.symptomsRecurrence) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: selectOne,
					path: ["symptomsRecurrence"],
				});
			}

			if (
				disallowedSymptomsRecurrence.includes(
					data.symptomsRecurrence as SymptomRecurrence,
				)
			) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: cannotStartTherapyWithLowReccurence,
					path: ["symptomsRecurrence"],
				});
			}
		});

export const getTreatmentSchema = (
	{ selectOne } = { selectOne: "Select one of the answers to proceed." },
) =>
	z
		.object({
			alreadyDiagnosedByDoctor: z.string({ message: selectOne }),
			triedOtherOptions: z.string({ message: selectOne }).optional(),
		})
		.superRefine((data, ctx) => {
			if (data.alreadyDiagnosedByDoctor === "true" && !data.triedOtherOptions) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: selectOne,
				});
			}
		});

export const getCannabisTreatmentSchema = (
	{ selectOne, cannotContinueWithNegativeEffects } = {
		selectOne: "Select one of the answers to proceed.",
		cannotContinueWithNegativeEffects:
			"You cannot continue treatment with negative effects",
	},
) =>
	z
		.object({
			alreadyPrescribedCannabisInGermany: z
				.string({ message: selectOne })
				.min(1),
			positiveEffects: z.array(z.string()).optional(),
			negativeEffects: z.array(z.string()).optional(),
		})
		.superRefine((data, ctx) => {
			if (data.alreadyPrescribedCannabisInGermany === "false") {
				// No further questions if the answer is "no"
				return;
			}

			if (
				!data.positiveEffects ||
				data.positiveEffects.length === 0 ||
				data.positiveEffects.includes("none")
			) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: selectOne,
					path: ["positiveEffects"],
				});
			}

			const showNegativeEffects =
				data.positiveEffects.length > 0 &&
				!data.positiveEffects.includes("none");

			if (!showNegativeEffects) {
				return;
			}

			if (!data.negativeEffects || data.negativeEffects.length === 0) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: selectOne,
					path: ["negativeEffects"],
				});
			}
			if (!data.negativeEffects.includes("none")) {
				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: cannotContinueWithNegativeEffects,
					path: ["negativeEffects"],
				});
			}
		});

export const getCheckoutDiagnoseSchema = (
	{
		selectOne,
		cannotStartTherapyWithLowReccurence,
		cannotStartTherapySoSoon,
		cannotContinueWithNegativeEffects,
	} = {
		selectOne: "Select one of the answers to proceed.",
		cannotContinueWithNegativeEffects:
			"You cannot continue treatment with negative effects",
		cannotStartTherapySoSoon:
			"You cannot start therapy so soon after the symptoms started",
		cannotStartTherapyWithLowReccurence:
			"You cannot start therapy when the symptoms happen so rarely",
	},
) => {
	const baseSchema = z.object({
		stepIndex: z.number().int().min(0),
		prescriptionOrderType: prescriptionOrderType,
		complaint: z.enum(checkoutDiagnoseComplaints).optional(),
		therapyEffects: therapyEffectsSchema.optional(),
		symptoms: getSymptomsSchema({
			selectOne,
			cannotStartTherapySoSoon,
			cannotStartTherapyWithLowReccurence,
		}).optional(),
		treatment: getTreatmentSchema({ selectOne }).optional(),
		cannabisTreatment: getCannabisTreatmentSchema({
			selectOne,
			cannotContinueWithNegativeEffects,
		}).optional(),
		hasExclusions: z
			.string({ message: selectOne })
			.refine((val) => val !== "true")
			.optional(),
		differentMedication: z
			.string({ message: selectOne })
			.refine((val) => val !== "true")
			.optional(),
	});

	return baseSchema.superRefine((data, ctx) => {
		const stepIndex = data.stepIndex;
		const prescriptionOrderType = data.prescriptionOrderType;

		const steps = getDiagnoseSteps(prescriptionOrderType);

		if (data.stepIndex < 0 || data.stepIndex >= steps.length) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Invalid step index",
			});
		}

		const currentStep = steps[stepIndex];
		if (!currentStep) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Invalid step index",
			});
			return;
		}

		const stepsToValidate = steps.slice(0, stepIndex + 1);

		for (const step of stepsToValidate) {
			const field = checkoutDiagnoseStepToSchema[step];
			const schema = baseSchema.shape[field].unwrap();
			const value = data[field];

			const result = schema.safeParse(value);

			if (!result.success) {
				for (const issue of result.error.issues) {
					ctx.addIssue({
						...issue,
						path: [field, ...(issue.path ?? [])],
					});
				}
			}
		}
	});
};

export type CheckoutDiagnose = z.infer<
	ReturnType<typeof getCheckoutDiagnoseSchema>
>;

export const checkoutStepSchema = z.enum(checkoutSteps);
export type CheckoutStep = z.infer<typeof checkoutStepSchema>;

export const checkoutStepsSchema = z.object({
	currentStep: checkoutStepSchema,
	completedSteps: z.array(checkoutStepSchema),
});
export type CheckoutSteps = z.infer<typeof checkoutStepsSchema>;

const prescriptionType = z.enum(["private"]);
export type PrescriptionType = z.infer<typeof prescriptionType>;

export const prescriptionOrderType = z.enum(["initial", "follow-up"]);
export type PrescriptionOrderType = z.infer<typeof prescriptionOrderType>;

export const prescriptionOrderSchema = z.object({
	prescriptionUpload: z.literal(false),
	prescriptionOrderType,
	complaint: z.enum(checkoutDiagnoseComplaints),
});
export type PrescriptionOrder = z.infer<typeof prescriptionOrderSchema>;

export const prescriptionUploadSchema = z.object({
	prescriptionUpload: z.literal(true),
	prescriptionFileKey: z.string(),
	prescriptionUrl: z.string(),
});
export type PrescriptionUpload = z.infer<typeof prescriptionUploadSchema>;

export const prescriptionSchema = z.discriminatedUnion("prescriptionUpload", [
	prescriptionOrderSchema,
	prescriptionUploadSchema,
]);

export type Prescription = z.infer<typeof prescriptionSchema>;

export const summarySchema = z.object({
	hasVideoCall: z.boolean(),
	hasTravelDocument: z.boolean(),
	vasItems: z
		.array(
			z.object({
				type: z.string(),
				price: z.number(),
				selectedAt: z.string().optional(),
			}),
		)
		.optional()
		.default([]),
	voucherCode: z
		.string()
		.nullable()
		.optional()
		.transform((val) => {
			if (val === "null") return null;
			if (val === "undefined") return undefined;
			return val;
		}),
	checkoutType: z.string().default("normal"),
	paymentType: z.enum(PAYMENT_TYPE),
});

/** Placeholder until OTC cart sync persists real selection state (OTC-27). */
export const crossSellSchema = z.object({
	visited: z.boolean().optional(),
});
export type CheckoutCrossSell = z.infer<typeof crossSellSchema>;

export const deliverySchema = z.object({
	confirmedGroups: z.array(
		z.object({
			groupId: z.string(),
			deliveryMethodUuid: z.string(),
		}),
	),
});

export type CheckoutDelivery = z.infer<typeof deliverySchema>;

export const baseCheckoutSchema = z.object({
	cart: cartSchema,
	checkoutSteps: checkoutStepsSchema,
	prescription: prescriptionSchema.optional(),
	diagnose: getCheckoutDiagnoseSchema().optional(),
	awareness: getCheckoutAwarenessSchema().optional(),
	// Defaulted rather than optional on purpose. checkoutStepToSchema maps the
	// completed cross-sell step to this key (default off, so every customer hits
	// this), so an absent value made the whole stored checkout unparseable:
	// CheckoutStore.get returned null, the provider re-initialised, and the
	// customer silently lost their questionnaire. A default degrades that to
	// "not visited" instead.
	crossSell: crossSellSchema.default({}),
	// Defaulted rather than optional on purpose. checkoutStepToSchema maps the
	// completed delivery step to this key, so an absent value made the whole
	// stored checkout unparseable: CheckoutStore.get returned null, the provider
	// re-initialised, and the customer silently lost their questionnaire. A
	// default degrades that to "no confirmations" instead.
	delivery: deliverySchema.default({ confirmedGroups: [] }),
	summary: summarySchema.optional(),
	expireAt: z.number().optional(),
});

export const checkoutSchema = baseCheckoutSchema.superRefine((data, ctx) => {
	const stepsToValidate = Array.from(
		new Set(
			data.checkoutSteps.completedSteps.map(
				(step) => checkoutStepToSchema[step],
			),
		),
	);

	//remove "prescription" from steps to validate as it's not ready yet
	if (
		data.checkoutSteps.currentStep === CHECKOUT_STEP_NAMES.prescriptionOrder
	) {
		stepsToValidate.splice(stepsToValidate.indexOf("prescription"), 1);
	}

	if (stepsToValidate.length === 0) {
		return;
	}

	for (const step of stepsToValidate) {
		const schema = unwrap(baseCheckoutSchema.shape[step]);
		const value = data[step];

		const result = schema.safeParse(value);

		if (!result.success) {
			for (const issue of result.error.issues) {
				ctx.addIssue({
					...issue,
					path: [step, ...(issue.path ?? [])],
				});
			}
		}
	}
});

export type Checkout = z.infer<typeof checkoutSchema>;
export type CheckoutInput = z.input<typeof checkoutSchema>;
